<template>
  <div>
    <AdminMemberSummary
      :member="currentMember"
      :show-ban-reason="canAccessSensitiveSections"
      class="mb-5"
    />

    <Accordion
      multiple
      :value="activePanels"
      @update:value="onPanelsUpdate"
    >
      <AccordionPanel value="0">
        <AccordionHeader>Membership</AccordionHeader>
        <AccordionContent>
          <div class="kb-rows">
            <!-- Member Type Section -->
            <AdminMemberTypeSection
              :key="`type-${currentMember.id}`"
              :member="currentMember"
              :current-type="currentMemberType"
              :associated-subkrewe="associatedSubkrewe"
              :read-only="isRedshirtAdmin"
              @type-change="handleAdminConfirmTypeChange"
            />

            <!-- SubKrewe Assignment Manager (now includes the association card) -->
            <AdminMemberAssignmentManager
              ref="assignmentManagerRef"
              :key="`assignment-${currentMember.id}`"
              :member="currentMember"
              :current-member-type="currentMemberType"
              :associated-subkrewe="associatedSubkrewe"
              :is-redshirt-admin="isRedshirtAdmin"
              @member-updated="afterMemberUpdate"
              @assignment-complete="afterMemberUpdate"
              @captain-promotion-cancelled="handleCaptainPromotionCancelled"
            />

            <AdminMemberLifetimeSection
              v-if="canAccessSensitiveSections"
              :key="`lifetime-${currentMember.id}`"
              :member="currentMember"
            />

            <AdminMemberGoldenWookieeSection
              v-if="canAccessSensitiveSections"
              :key="`golden-wookiee-${currentMember.id}`"
              :member="currentMember"
            />
          </div>
        </AccordionContent>
      </AccordionPanel>

      <AccordionPanel
        v-if="canAccessSensitiveSections"
        value="1"
      >
        <AccordionHeader>{{ currentYear }} Parade</AccordionHeader>
        <AccordionContent>
          <div class="kb-rows">
            <AdminMemberRollingStatusSection
              :key="`rolling-${currentMember.id}`"
              :member="currentMember"
              @updated="afterMemberUpdate"
            />

            <AdminMemberPaymentStatusSection
              :key="`payment-${currentMember.id}`"
              :member="currentMember"
              @updated="afterMemberUpdate"
            />

            <AdminMemberDiscountSection
              v-if="!isRedshirt(currentMember)"
              :key="`discount-${currentMember.id}`"
              :member="currentMember"
            />

            <AdminMemberCheckinStatusSection
              :key="`checkin-${currentMember.id}`"
              :member="currentMember"
              @updated="afterMemberUpdate"
            />
          </div>
        </AccordionContent>
      </AccordionPanel>

      <AccordionPanel
        v-if="canAccessSensitiveSections && !isRedshirt(currentMember)"
        value="2"
      >
        <AccordionHeader>Children</AccordionHeader>
        <AccordionContent>
          <div class="pt-3">
            <ChildrenList
              :key="`children-${currentMember.id}`"
              :children="currentMember.children || {}"
              :member="currentMember"
              :in-admin-context="true"
              :disabled="isRedshirt(currentMember)"
              @add="handleAddChild"
              @update="handleUpdateChild"
              @remove="handleRemoveChild"
            />
          </div>
        </AccordionContent>
      </AccordionPanel>

      <AccordionPanel
        v-if="canAccessSensitiveSections"
        value="3"
      >
        <AccordionHeader>Tickets &amp; Purchases</AccordionHeader>
        <AccordionContent>
          <div class="pt-1">
            <AdminMemberPurchasesSection
              :key="`purchases-${currentMember.id}`"
              :member="currentMember"
            />
          </div>
        </AccordionContent>
      </AccordionPanel>

      <AccordionPanel
        v-if="canAccessSensitiveSections"
        value="4"
      >
        <AccordionHeader>Admin Settings</AccordionHeader>
        <AccordionContent>
          <!-- Admin Settings Section -->
          <AdminMemberAdminSection
            v-if="canAccessSensitiveSections"
            :key="`admin-${currentMember.id}`"
            :member="currentMember"
          />
          <!-- Redshirt Lieutenant Toggle intentionally omitted in this view; managed elsewhere -->
        </AccordionContent>
      </AccordionPanel>

      <AccordionPanel value="5">
        <AccordionHeader>Member Details</AccordionHeader>
        <AccordionContent>
          <div class="pt-1 flex flex-col gap-4">
            <!-- Authentication Status (hidden for redshirt admins) -->
            <AdminMemberAuthStatusSection
              v-if="!isRedshirtAdmin"
              :key="`auth-${currentMember.id}`"
              :member="currentMember"
            />

            <!-- Member Details Form -->
            <div :class="isRedshirtAdmin ? 'pointer-events-none opacity-60' : ''">
              <AdminMemberDetailsForm
                :key="currentMember.id"
                :member="currentMember"
                @update:title="title => emit('update:title', title)"
                @save="afterMemberUpdate"
              />
            </div>
            <div
              v-if="isRedshirtAdmin"
              class="kb-field-hint mt-2"
            >
              Read-only for red shirt admins
            </div>
          </div>
        </AccordionContent>
      </AccordionPanel>

      <AccordionPanel
        v-if="canViewHistory"
        value="6"
      >
        <AccordionHeader>History</AccordionHeader>
        <AccordionContent>
          <template v-if="historyOpen">
            <h4 class="kb-label mt-3 mb-1">
              Changes to this member
            </h4>
            <AdminAuditHistory
              :key="`history-${currentMember.id}`"
              :ref-path="`members/${currentMember.id}`"
            />
            <h4 class="kb-label mt-6 mb-1">
              Changes made by this member
            </h4>
            <AdminAuditHistory
              :key="`actor-history-${currentMember.id}`"
              :actor-uid="currentMember.id"
            />
          </template>
        </AccordionContent>
      </AccordionPanel>
    </Accordion>

    <!-- T-shirt size dialog for redshirt type change -->
    <DialogsTshirtSize
      v-model="showTshirtSizeDialog"
      :current-size="pendingRedshirtMember?.tshirt_size"
      :allow-skip="true"
      @confirm="onTshirtSizeConfirmed"
      @skip="onTshirtSizeSkipped"
    />
  </div>
</template>

<script setup lang="ts">
import type { SubscriptionCallbackMutation } from 'pinia'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import type { Member } from '~/shared/types/member'
import { isRedshirt } from '~/shared/types/memberType'
import type { SubKrewe } from '~/shared/types/subkrewe'
import type { TshirtSize } from '~/shared/types/tshirtSize.js'
import { canViewAuditHistory } from '~/shared/utils/grantPrivileges.js'
import { memberIsAdmin, memberIsRedshirtAdmin } from '~/shared/utils/memberUtils'
import type { Child } from '~/shared/types/member.js'
import type { MemberTypeValue } from '~/shared/types/memberType.js'

/** A child plus the record key it is stored under — `children` is keyed by id. */
type ChildWithId = Child & { id: string }

// Define properly typed functions for the subscriptions
type PiniaUnsubscribe = () => void

// Define a type for the exposed methods
interface SubkreweAssignmentManagerInstance {
  showAssignDialog: (isCaptainPromoting?: boolean) => void
  resetDialogState: () => void
}

// Define the array with proper typing
const unsubscribers: PiniaUnsubscribe[] = []

const emit = defineEmits(['update:data', 'update:title'])

interface Props {
  data: Member
}

const props = defineProps<Props>()
const membersStore = useMembersStore()
const subkrewesStore = useSubKrewesStore()
const toast = useToast()
const confirm = useConfirm()
const { paradeYear: currentYear } = useParadeYear()

// Get current user for permission checking
const loggedInMember = await useCurrentMember()

// Permission checking - only allow full admins access to sensitive sections
const isRedshirtAdmin = computed(() => memberIsRedshirtAdmin(loggedInMember))
const canAccessSensitiveSections = computed(() => {
  // Only full admins can access admin settings (role management, deletion, etc.)
  // Redshirt admins should never see these controls
  return memberIsAdmin(loggedInMember)
})

const currentUser = useCurrentUser()
const canViewHistory = computed(() => canViewAuditHistory(loggedInMember, currentUser.value))

// Refs for child components with proper typing
const assignmentManagerRef = ref<SubkreweAssignmentManagerInstance | null>(null)

// State management refs - declare early to avoid initialization issues
const currentMemberType = ref<MemberTypeValue>(props.data.member_type || 'member')
const blockAutoSaveUntil = ref(0)
const pendingRoleChange = ref(false)

// T-shirt size dialog state for redshirt type changes
const showTshirtSizeDialog = ref(false)
const pendingRedshirtMember = ref<Member | null>(null)
const pendingRedshirtTypeChange = ref<{ currentType: string, hasSubkrewe: boolean } | null>(null)

// Use ref instead of computed to avoid reactivity issues when switching members
const currentMember = ref<Member>(props.data)

// Update member data when props change (member switching)
watch(() => props.data.id, (newId, oldId) => {
  if (newId && newId !== oldId) {
    // Clear any pending auto-save operations from previous member
    blockAutoSaveUntil.value = Date.now() + 2000
    pendingRoleChange.value = false

    // Always prefer the canonical member from store
    const path = `members/${newId}`
    const storeMember = membersStore.members[path]
    currentMember.value = storeMember || props.data

    // The member_type watcher skips the window set above, so take the new
    // member's type here; Assign and Remove act on it
    currentMemberType.value = currentMember.value.member_type || 'member'
    pendingRedshirtMember.value = null
    pendingRedshirtTypeChange.value = null

    // Reset UI state for new member
    if (assignmentManagerRef.value) {
      assignmentManagerRef.value.resetDialogState()
    }
  }
}, { immediate: true })

// Also watch for when the same member ID but different object reference is passed
// This happens when search results provide processed member objects
watch(() => props.data, (newData) => {
  if (newData && newData.id === currentMember.value?.id) {
    // Same member but potentially different object reference
    // Always prefer the canonical version from store
    const path = `members/${newData.id}`
    const storeMember = membersStore.members[path]
    if (storeMember && storeMember !== currentMember.value) {
      // Log when we detect object reference inconsistencies in development
      console.warn('DrawerContent: Detected different object reference for same member, using canonical store version')
      currentMember.value = storeMember
    }
  }
}, { immediate: true })

// Watch for store updates to the current member
watch(() => {
  if (currentMember.value?.id) {
    const path = `members/${currentMember.value.id}`
    return membersStore.members[path]
  }
  return null
}, (updatedMember) => {
  if (updatedMember && updatedMember.id === currentMember.value?.id) {
    currentMember.value = updatedMember
  }
}, { deep: true })

const associatedSubkrewe = computed<SubKrewe | null>(() => {
  // Get the subkrewe reference from the member
  const memberSubkrewe = currentMember.value?.subkrewe

  // If no reference exists, return null
  if (!memberSubkrewe || !memberSubkrewe.id) return null

  // Look up the full subkrewe object from the store using the ID
  const path = `subkrewes/${memberSubkrewe.id}`
  const storeSubkrewe = subkrewesStore.subkrewes[path]

  // Return the store version if available or fall back to the member's reference
  return storeSubkrewe || memberSubkrewe
})

// Panel state management
const getStoredPanelState = (): string[] => {
  const stored = localStorage.getItem('member-drawer-panels')
  if (stored) {
    try {
      return JSON.parse(stored)
    }
    catch (e) {
      console.error('Error parsing stored panel state:', e)
      return ['0', '1']
    }
  }
  return ['0', '1'] // Default to first two panels open
}

const activePanels = ref<string | string[] | null | undefined>(getStoredPanelState())

const onPanelsUpdate = (newValue: string | string[] | null | undefined) => {
  activePanels.value = newValue
  localStorage.setItem('member-drawer-panels', JSON.stringify(newValue))
}

// Only query the audit log once the History panel is opened.
const historyOpen = computed(() => [activePanels.value].flat().includes('6'))

// After member update from form or other operations
const afterMemberUpdate = () => {
  // Prevent updates during pending operations or for different members
  if (pendingRoleChange.value || Date.now() < blockAutoSaveUntil.value) {
    return
  }

  // Get fresh data from store for current member only
  const updatedMember = membersStore.membersArray.find(m => m.id === currentMember.value?.id)

  if (updatedMember && updatedMember.id === currentMember.value?.id) {
    // Update current member ref
    currentMember.value = updatedMember

    if (updatedMember.member_type) {
      currentMemberType.value = updatedMember.member_type
    }

    // Emit an event with the updated member data
    emit('update:data', updatedMember)

    // Force a full component refresh
    nextTick(() => {
      // This access forces Vue to re-evaluate the computed property
      const _forceUpdate = associatedSubkrewe.value
    })
  }
}

// Member type change handler
const handleAdminConfirmTypeChange = ({ currentType, newType, hasSubkrewe }: {
  currentType: MemberTypeValue
  newType: MemberTypeValue
  hasSubkrewe: boolean
}) => {
  // Special handling for captain promotion
  if (newType === 'captain') {
    if (assignmentManagerRef.value) {
      assignmentManagerRef.value.showAssignDialog(true)
    }
    return
  }

  // Regular confirmation dialog for other member type changes
  let message = ''
  let header = ''

  if (newType === 'redshirt') {
    header = 'Make red shirt'
    message = `Change ${props.data.name} to a red shirt?`
    if (currentType === 'captain') {
      message += ' They will no longer be a captain of their SubKrewe.'
    }
  }
  else if (newType === 'member') {
    header = 'Make member'
    message = `Change ${props.data.name} to a regular member?`
    if (currentType === 'captain') {
      message += ' They will no longer be a captain of their SubKrewe but will remain a member.'
    }
  }

  confirm.require({
    message,
    header,
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: header,
    rejectLabel: 'Cancel',
    accept: async () => {
      // If changing to redshirt and no t-shirt size, prompt for it
      if (newType === 'redshirt' && !props.data.tshirt_size) {
        pendingRedshirtMember.value = props.data
        pendingRedshirtTypeChange.value = { currentType, hasSubkrewe }
        showTshirtSizeDialog.value = true
        return
      }

      // Execute the type change directly
      await executeTypeChange(newType)
    },
  })
}

// Execute the actual type change
const executeTypeChange = async (newType: MemberTypeValue, tshirtSize?: TshirtSize) => {
  try {
    // Block auto-save during changes
    blockAutoSaveUntil.value = Date.now() + 10000
    pendingRoleChange.value = true

    const memberToChange = pendingRedshirtMember.value || props.data

    // If we have a t-shirt size to set, update the member first
    if (tshirtSize) {
      await membersStore.updateMember(memberToChange, { tshirt_size: tshirtSize })
    }

    const success = await membersStore.changeMemberType(memberToChange, newType)

    if (success) {
      await nextTick()

      // Update local state
      currentMemberType.value = newType

      toast.add({
        severity: 'success',
        summary: 'Member Type Changed',
        detail: `${memberToChange.name} is now a ${newType}`,
        life: 3000,
      })

      // Run component update logic
      afterMemberUpdate()
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update member type',
        life: 3000,
      })
    }
  }
  catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    // Reset state after a delay
    setTimeout(() => {
      pendingRoleChange.value = false
      pendingRedshirtMember.value = null
      pendingRedshirtTypeChange.value = null
    }, 500)
  }
}

// Handle t-shirt size confirmation
const onTshirtSizeConfirmed = async (size: TshirtSize) => {
  await executeTypeChange('redshirt', size)
}

// Handle t-shirt size skip
const onTshirtSizeSkipped = async () => {
  await executeTypeChange('redshirt')
}

// Handle captain promotion cancellation
const handleCaptainPromotionCancelled = () => {
  pendingRoleChange.value = false

  // Reset member type if it was changed in the UI
  if (currentMemberType.value === 'captain') {
    // Restore original member type from the initial data
    currentMemberType.value = props.data.member_type || 'member'

    // If the member type was already changed in the store, revert it
    const memberPath = `members/${props.data.id}`
    if (membersStore.members[memberPath]?.member_type === 'captain') {
      membersStore.changeMemberType(props.data, props.data.member_type || 'member')
        .catch(error => console.error('Error reverting member type:', error))
    }
  }
}

// Handle children management functions
const handleAddChild = async (child: ChildWithId) => {
  try {
    if (!currentMember.value?.id) return

    const success = await membersStore.addChildToMember(currentMember.value, child)

    if (success) {
      toast.add({
        severity: 'success',
        summary: 'Child Added',
        detail: `${child.firstName} ${child.lastName} has been added to ${currentMember.value.name}'s children`,
        life: 3000,
      })

      // Refresh member data
      afterMemberUpdate()
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to add child',
        life: 3000,
      })
    }
  }
  catch (error) {
    console.error('Error adding child:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'An error occurred while adding the child',
      life: 3000,
    })
  }
}

const handleUpdateChild = async (child: ChildWithId) => {
  try {
    if (!currentMember.value?.id) return

    const success = await membersStore.updateChildForMember(currentMember.value, child)

    if (success) {
      toast.add({
        severity: 'success',
        summary: 'Child Updated',
        detail: `${child.firstName} ${child.lastName}'s information has been updated`,
        life: 3000,
      })

      // Refresh member data
      afterMemberUpdate()
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update child information',
        life: 3000,
      })
    }
  }
  catch (error) {
    console.error('Error updating child:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'An error occurred while updating the child',
      life: 3000,
    })
  }
}

const handleRemoveChild = async (child: ChildWithId) => {
  try {
    if (!currentMember.value?.id) return

    const success = await membersStore.removeChildFromMember(currentMember.value, child.id)

    if (success) {
      toast.add({
        severity: 'success',
        summary: 'Child Removed',
        detail: `${child.firstName} ${child.lastName} has been removed`,
        life: 3000,
      })

      // Refresh member data
      afterMemberUpdate()
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to remove child',
        life: 3000,
      })
    }
  }
  catch (error) {
    console.error('Error removing child:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'An error occurred while removing the child',
      life: 3000,
    })
  }
}

// Watch for changes in member type
watch(() => currentMember.value?.member_type, (newType, _oldType) => {
  // If the form is in a pending member type change, don't let props override it
  if (pendingRoleChange.value || Date.now() < blockAutoSaveUntil.value) {
    return
  }

  // Otherwise, normal behavior - update the current type
  if (newType) currentMemberType.value = newType
})

// Watch for subkrewe changes to trigger UI updates
watch(() => currentMember.value?.subkrewe, () => {
  // Force a component refresh whenever the subkrewe changes directly
  nextTick(() => {
    // This is intentionally empty, just triggers the refresh
  })
})

// Clean up subscriptions when component unmounts or member changes
const cleanupSubscriptions = () => {
  unsubscribers.forEach(unsubscribe => unsubscribe())
  unsubscribers.length = 0
}

// Watch for member changes to clean up old subscriptions
watch(() => currentMember.value?.id, (newId, oldId) => {
  if (newId !== oldId) {
    // Clean up old subscriptions when member changes
    cleanupSubscriptions()
  }
})

onMounted(() => {
  // Initialize current member type
  currentMemberType.value = currentMember.value?.member_type || 'member'

  // Load members for the current subkrewe if available
  if (associatedSubkrewe.value) {
    subkrewesStore.loadSubkreweMembers(associatedSubkrewe.value)
      .catch(error => console.error('Error loading members:', error))
  }

  // Subscribe to member changes to update UI
  const memberSubscribe: PiniaUnsubscribe = membersStore.$subscribe(
    (mutation: SubscriptionCallbackMutation<typeof membersStore.$state>, state: typeof membersStore.$state) => {
      if (currentMember.value?.id) {
        const memberPath = `members/${currentMember.value.id}`
        const updatedMember = state.members[memberPath]

        // Only update if this is the same member we're currently viewing
        if (updatedMember && updatedMember.id === currentMember.value.id) {
          // Prevent recursive updates during pending operations
          if (!pendingRoleChange.value && Date.now() >= blockAutoSaveUntil.value) {
            currentMember.value = updatedMember
          }
        }
      }
    })

  unsubscribers.push(memberSubscribe)
})

onUnmounted(() => {
  // Clean up subscriptions
  cleanupSubscriptions()
})
</script>
